import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Input,
  Card,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { 
  Spin, 
  Tooltip, 
  Empty, 
  Upload, 
  Popover,
  Timeline,
  Badge as AntBadge,
  Tag
} from "antd";
import { InfoCircleOutlined, UploadOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import { Icon } from "../../components/Component";
import { expensesApis } from "../../api/Expenses";

interface ApprovalData {
  expenseApprovalHierarchyId?: number;
  expenseApprovalId?: number;
  orgId: number;
  projectId: number;
  reportId: number;
  userId: number;
  roleId: number;
  level: string;
  approvalStage: string;
  createdBy: number;
  createdAt: string;
  userName?: string;
  roleName?: string;
  status?: string;
  reason?: string;
  actionDate?: string;
}

interface ExpenseReport {
  id: string;
  reportName: string;
  status: string;
  submittedDate: string;
  approvedDate?: string;
  totalAmount: number;
  expenses: number;
  total: string;
  amountToBeReimbursed: string;
  submitter?: string;
  dateRange?: string;
  totalIn?: number;
  totalOut?: number;
  balance?: number;
  reimbursableAmount?: number;
  nonReimbursableAmount?: number;
  taxAmount?: number;
  createdBy?: string;
  createdByName?: string;
  approvalData?: ApprovalData[];
  // User-specific fields from fetchReports API
  userTotalIn?: number;
  userTotalOut?: number;
  userBalance?: number;
  userReimbursableAmount?: number;
  userReimbursableBalance?: number;
  // Pending-specific fields from fetchReport API
  pendingUserTotalIn?: number;
  pendingUserTotalOut?: number;
  pendingUserBalance?: number;
  pendingUserReimbursableAmount?: number;
  pendingUserNonReimbursableAmount?: number;
  pendingUserTaxAmount?: number;
  pendingReimbursableTaxAmount?: number;
  pendingUserReimbursableTaxAmount?: number;
  // Data fetching optimization flags
  dataAlreadyFetched?: boolean;
  fetchedReportData?: any;
  fetchedExpensesData?: any;
  // Extra data passed from parent components
  extra?: {
    status?: string;
    [key: string]: any;
  };
}

interface Expense {
  id: string;
  expenseId: number;
  date: string;
  category: string;
  description: string;
  amount: number;
  receipt?: string;
  status: 'pending' | 'approved' | 'rejected';
  merchant?: string;
  paymentType?: string;
  paymentMode?: string;
  paymentStatus?: string;
  actionDate?: string;
  claimReimbursement?: boolean;
  taxType?: string;
  currencyId?: number;
  referenceId?: string;
  productCategory?: string;
  gstin?: string;
  destinationOfSupply?: string;
  items?: ExpenseItem[];
  taxAmount?: number;
  reimbursableAmount?: number;
  expenseApprovals?: ApprovalData[];
  createdBy?: string; // User who created the expense
}

interface ExpenseItem {
  expenseId: number;
  itemId: number;
  amount: number;
  category: string;
  description: string;
  taxPercent: number;
  sacCode?: string;
  amountWithoutTax: number;
  createdBy: number;
}

interface ReportBreakDownProps {
  report: ExpenseReport | null;
  onBack: () => void;
  projectName?: string;
  isModalView?: boolean;
  refreshData?: boolean;
  onRefreshComplete?: () => void;
  projectId?: number;
  sourcePage?: string;
  allowEdit?: boolean;
  onFreshDataUpdate?: (freshData: any) => void;
}

// ApprovalTimelineContent component for displaying approval hierarchy
const ApprovalTimelineContent = ({ approvalsData }: { approvalsData: ApprovalData[] }) => {
  if (!approvalsData || approvalsData?.length === 0) {
    return <AntBadge color="outline-danger">No</AntBadge>;
  }

  return (
    <Popover
      placement="rightTop"
      content={
        <Timeline className="approval-timeline">
          {approvalsData?.map((approval: any, index: number) => {
            const isApproved = approval.status === "Approved";
            const isRejected = approval.status === "Rejected";
            return (
              <Timeline.Item
                key={index}
                dot={
                  isApproved ? (
                    <CheckCircleOutlined style={{ color: "green" }} />
                  ) : isRejected ? (
                    <CloseCircleOutlined style={{ color: "red" }} />
                  ) : undefined
                }
              >
                <div style={{ marginBottom: '4px' }}>
                  <strong style={{ color: "#2557d6" }}>
                    {approval?.userName || approval?.name || `User ${approval?.userId || 'Unknown'}`}
                  </strong>
                </div>
                {approval?.roleName && (
                  <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                    Role: {approval.roleName}
                  </div>
                )}
                {approval?.approvalStage && (
                  <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                    Stage: {approval.approvalStage}
                  </div>
                )}
                <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                  Level: {approval?.level || `Level ${index + 1}`}
                </div>
                {approval?.actionDate || approval?.createdAt && (
                  <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                    Date: {dayjs(approval?.actionDate || approval?.createdAt).format("DD-MMM-YYYY")}
                  </div>
                )}
                {approval?.reason && (
                  <div style={{ marginTop: '4px' }}>
                    <Icon
                      name="info"
                      className="ms-1"
                      title={`Reason : ${approval?.reason}`}
                    />
                  </div>
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      }
      title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
    >
      <span
        style={{
          color: "#2557d6",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Yes
      </span>
    </Popover>
  );
};

// ExpenseApprovalTooltip component for displaying expense approval details
const ExpenseApprovalTooltip = ({ expenseApprovals }: { expenseApprovals: ApprovalData[] }) => {
  if (!expenseApprovals || expenseApprovals.length === 0) {
    return null;
  }

  return (
    <Popover
      placement="rightTop"
      content={
        <Timeline className="approval-timeline">
          {expenseApprovals?.map((approval: any, index: number) => {
            const isApproved = approval.status === "Approved";
            const isRejected = approval.status === "Rejected";
            return (
              <Timeline.Item
                key={approval.expenseApprovalId || approval.expenseApprovalHierarchyId || index}
                dot={
                  isApproved ? (
                    <CheckCircleOutlined style={{ color: "green" }} />
                  ) : isRejected ? (
                    <CloseCircleOutlined style={{ color: "red" }} />
                  ) : undefined
                }
              >
                <div style={{ marginBottom: '4px' }}>
                  <strong style={{ color: "#2557d6" }}>
                    {approval?.userName || approval?.name || `User ${approval?.userId || 'Unknown'}`}
                  </strong>
                </div>
                {approval?.roleName && (
                  <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                    Role: {approval.roleName}
                  </div>
                )}
                {approval?.approvalStage && (
                  <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                    Stage: {approval.approvalStage}
                  </div>
                )}
                <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                  Level: {approval?.level || `Level ${index + 1}`}
                </div>
                {approval?.actionDate || approval?.createdAt && (
                  <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                    Date: {dayjs(approval?.actionDate || approval?.createdAt).format("DD-MMM-YYYY")}
                  </div>
                )}
                {approval?.reason && (
                  <div style={{ marginTop: '4px' }}>
                    <Icon
                      name="info"
                      className="ms-1"
                      title={`Reason : ${approval?.reason}`}
                    />
                  </div>
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      }
      title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
    >
      <span
        style={{
          color: "#2557d6",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Yes
      </span>
    </Popover>
  );
};


const ReportBreakDown: React.FC<ReportBreakDownProps> = ({ report, onBack, projectName, isModalView = false, refreshData = false, onRefreshComplete, projectId: propProjectId, sourcePage, allowEdit = false, onFreshDataUpdate }) => {
  const methods = useForm({
    mode: "onChange",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  
  // Get projectId from prop first, then from location state
  const { id: locationProjectId } = location.state || {};
  const projectId = propProjectId || locationProjectId;

  // Helper function to check if current user is in approval hierarchy for an expense
  const isCurrentUserInApprovalHierarchy = (expenseApprovals: ApprovalData[]) => {
    if (!expenseApprovals || expenseApprovals.length === 0 || !currentUser) {
      return false;
    }
    
    const currentUserId = currentUser.id || currentUser.userId;
    const isInHierarchy = expenseApprovals.some(approval => 
      (approval.userId === currentUserId) && 
      (approval.status === 'Pending' || approval.status === 'pending')
    );
    
    
    return isInHierarchy;
  };

  // Helper function to check if expense can be approved by current user
  const canCurrentUserApproveExpense = (expense: any) => {
    if (!currentUser || !expense) {
      return false;
    }

    // Check if expense status is pending
    const isExpensePending = expense.paymentStatus?.toLowerCase() === 'pending';
    
    // Check if current user is in approval hierarchy and has pending approval
    const isUserInHierarchy = isCurrentUserInApprovalHierarchy(expense.expenseApprovals || []);
    
    const canApprove = isExpensePending && isUserInHierarchy;
    
    
    return canApprove;
  };

  // Helper function to check if checkbox should be disabled
  const isCheckboxDisabled = (expense: any) => {
    if (!expense) {
      return true;
    }

    const status = expense.paymentStatus?.toLowerCase();
    const currentUserId = currentUser?.id || currentUser?.userId;
    
    // Check report-level status first (except for pendingApproval source)
    if (sourcePage !== "pendingApproval") {
      const reportStatus = reportData?.userReportStatus || reportData?.reportStatus || report?.status;
      if (reportStatus?.toLowerCase() === 'pending') {
        return true; // Disable all checkboxes when report status is pending
      }
    }
    
    // When coming from pending approvals, enable checkbox only if:
    // 1. Expense status is pending AND
    // 2. Current user is in approval hierarchy with pending status AND
    // 3. Current user hasn't already approved this expense
    if (sourcePage === "pendingApproval") {
      const isExpensePending = status === 'pending';
      const isUserInHierarchy = isCurrentUserInApprovalHierarchy(expense.expenseApprovals || []);
      
      // Check if current user has already approved this expense
      const hasUserApproved = expense.expenseApprovals?.some((approval: any) => 
        approval.userId === currentUserId && 
        (approval.status?.toLowerCase() === 'approved' || approval.status?.toLowerCase() === 'approve')
      );
      
      // Enable checkbox only if expense is pending, user is in hierarchy, and user hasn't approved yet
      return !(isExpensePending && isUserInHierarchy && !hasUserApproved);
    }

    // For other pages, handle different statuses
    if (status === 'approved') {
      return true; // Always disable for approved expenses
    }
    
    if (status === 'draft') {
      return false; // Always enable for draft expenses
    }
    
    if (status === 'rejected') {
      return false; // Always enable for rejected expenses
    }
    
    if (status === 'pending') {
      // For pending expenses, enable only if current user is in approval hierarchy
      return !isCurrentUserInApprovalHierarchy(expense.expenseApprovals || []);
    }
    
    // Enable only if current user can approve this expense
    return !canCurrentUserApproveExpense(expense);
  };
  
  const [activeTab, setActiveTab] = useState('expenses');
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  
  // Approval/Rejection states
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  
  // Bulk approval/rejection states
  const [isBulkRejectModalOpen, setIsBulkRejectModalOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');

  // Drag and drop states
  const [isDragOver, setIsDragOver] = useState(false);

  // Selection states
  const [selectedExpenses, setSelectedExpenses] = useState<string[]>([]);


  // Detail view states
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedExpenseForDetail, setSelectedExpenseForDetail] = useState<Expense | null>(null);
  const [isDetailViewMode, setIsDetailViewMode] = useState<'view' | 'edit'>('view');

  const [loading, setLoading] = useState(false);

  // Mock data for detailed modal - dynamically set based on report
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [advances, setAdvances] = useState<Expense[]>([]);
  
  // Fetch report data state
  const [reportData, setReportData] = useState<any>(null);
  
  // User mapping state
  const [userMapping, setUserMapping] = useState<{[key: string]: string}>({});
  
  // Lazy loading states - following Ledger.tsx pattern
  const [expensesCurrentPage, setExpensesCurrentPage] = useState(0);
  const [advancesCurrentPage, setAdvancesCurrentPage] = useState(0);
  const [expensesEndOfList, setExpensesEndOfList] = useState(false);
  const [advancesEndOfList, setAdvancesEndOfList] = useState(false);
  const [expensesPaginationId, setExpensesPaginationId] = useState<any>(null);
  const [advancesPaginationId, setAdvancesPaginationId] = useState<any>(null);
  const expensesLastEntryRef = useRef<HTMLDivElement | null>(null);
  const advancesLastEntryRef = useRef<HTMLDivElement | null>(null);
  const isInitialDataLoading = useRef(false); // Prevent multiple simultaneous API calls
  const isLoadingMoreExpenses = useRef(false); // Prevent multiple simultaneous loadMoreExpenses calls
  const isLoadingMoreAdvances = useRef(false); // Prevent multiple simultaneous loadMoreAdvances calls
  const itemsPerPage = 100;

  // Function to fetch and map user information
  const fetchUserMapping = async () => {
    if (!projectId) return;
    
    try {
      const response = await expensesApis.fetchProjectUsers(projectId);
      if (response && response.status && response.data) {
        const usersList = response.data || [];
        const mapping: {[key: string]: string} = {};
        
        usersList.forEach((user: any) => {
          const userId = user.userId?.toString() || user.id?.toString();
          const userName = user.userName || user.name || user.email || 'Unknown User';
          if (userId) {
            mapping[userId] = userName;
          }
        });
        
        setUserMapping(mapping);
      }
    } catch (error) {
      // Silently fail - user mapping is not critical
    }
  };

  // Helper function to get user name from various fields
  const getUserName = (expense: any) => {
    // First try to get the name directly
    if (expense.createdByName || expense.userName || expense.createdByUser) {
      return expense.createdByName || expense.userName || expense.createdByUser;
    }
    
    // If we only have a user ID, try to map it to a name
    if (expense.createdBy && userMapping[expense.createdBy]) {
      return userMapping[expense.createdBy];
    }
    
    // Fallback to the original value
    return expense.createdBy || 'Unknown User';
  };

  // Helper function to transform API response to Expense interface
  const transformExpenseData = (expense: any): Expense => ({
    id: expense.expenseId?.toString() || Date.now().toString(),
    expenseId: expense.expenseId,
    date: expense.actionDate ? dayjs(expense.actionDate).format('DD-MMM-YYYY') : dayjs().format('DD-MMM-YYYY'),
    category: expense.category || (expense.paymentType === 'Reimbursement' ? 'Reimbursement' : 'Unknown'),
    description: expense.description || '',
    amount: expense.amount || 0,
    status: expense.paymentStatus?.toLowerCase() === 'draft' ? 'pending' :
      expense.paymentStatus?.toLowerCase() === 'approved' ? 'approved' : 'rejected',
    merchant: expense.merchant,
    paymentType: expense.paymentType,
    paymentMode: expense.paymentMode,
    paymentStatus: expense.paymentStatus,
    actionDate: expense.actionDate,
    claimReimbursement: expense.claimReimbursement,
    taxType: expense.taxType,
    currencyId: expense.currencyId,
    referenceId: expense.referenceId,
    productCategory: expense.productCategory,
    gstin: expense.gstin,
    destinationOfSupply: expense.destinationOfSupply,
    items: expense.items || [],
    expenseApprovals: expense.expenseApprovals || [],
    createdBy: getUserName(expense)
  });

  // Lazy Loading: Intersection Observer for expenses infinite scroll
  useEffect(() => {
    if (!expensesLastEntryRef.current || expensesEndOfList || expenses.length < itemsPerPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          setExpensesCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    observer.observe(expensesLastEntryRef.current);
    return () => observer.disconnect();
  }, [expensesEndOfList, loading, itemsPerPage]);

  // Lazy Loading: Intersection Observer for advances infinite scroll
  useEffect(() => {
    if (!advancesLastEntryRef.current || advancesEndOfList || advances.length < itemsPerPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          setAdvancesCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    observer.observe(advancesLastEntryRef.current);
    return () => observer.disconnect();
  }, [advancesEndOfList, loading, itemsPerPage]);

  // Pagination: Load more expenses when page changes
  useEffect(() => {
    if (expensesCurrentPage > 1 && report?.id && projectId && !expensesEndOfList && !loading) {
      loadMoreExpenses();
    }
  }, [expensesCurrentPage, report?.id, projectId, expensesEndOfList, loading]);

  // Pagination: Load more advances when page changes
  useEffect(() => {
    if (advancesCurrentPage > 1 && report?.id && projectId && !advancesEndOfList && !loading) {
      loadMoreAdvances();
    }
  }, [advancesCurrentPage, report?.id, projectId, advancesEndOfList, loading]);

  // User Management: Fetch user mapping and update expenses when component mounts or projectId changes
  useEffect(() => {
    if (projectId) {
      fetchUserMapping();
    }
  }, [projectId]);

  // User Management: Update expenses with proper user names when user mapping changes
  useEffect(() => {
    if (Object.keys(userMapping).length > 0 && (expenses.length > 0 || advances.length > 0)) {
      // Only update if user mapping has actually changed and we have data
      const hasExpensesWithMissingNames = expenses.some(expense => 
        !expense.createdBy || expense.createdBy === 'Unknown User' || typeof expense.createdBy === 'number'
      );
      const hasAdvancesWithMissingNames = advances.some(advance => 
        !advance.createdBy || advance.createdBy === 'Unknown User' || typeof advance.createdBy === 'number'
      );
      
      if (hasExpensesWithMissingNames) {
        setExpenses(prevExpenses => 
          prevExpenses.map(expense => ({
            ...expense,
            createdBy: getUserName(expense)
          }))
        );
      }
      
      if (hasAdvancesWithMissingNames) {
        setAdvances(prevAdvances => 
          prevAdvances.map(advance => ({
            ...advance,
            createdBy: getUserName(advance)
          }))
        );
      }
    }
  }, [userMapping]);

  // Data Loading: Main data loading effect for expenses and advances
  useEffect(() => {
    if (report && report.id && projectId) {
      isInitialDataLoading.current = false;
      
      if (refreshData) {
        setExpenses([]);
        setAdvances([]);
        setExpensesCurrentPage(1);
        setAdvancesCurrentPage(1);
        setExpensesEndOfList(false);
        setAdvancesEndOfList(false);
        setExpensesPaginationId(null);
        setAdvancesPaginationId(null);
        
        if (onRefreshComplete) {
          onRefreshComplete();
        }
        
        setTimeout(() => {
          loadInitialData();
        }, 100);
      } else {
        setExpensesCurrentPage(1);
        setAdvancesCurrentPage(1);
        
        if (expenses.length === 0 && advances.length === 0) {
          setTimeout(() => {
            loadInitialData();
          }, 50);
        }
      }
    }
  }, [report?.id, refreshData, projectId]);


  // Helper: only append projectId to path when valid (never "null" in URL)
  const withProjectId = (basePath: string) =>
    projectId != null && !isNaN(Number(projectId)) ? `${basePath}/${projectId}` : basePath;

  // Helper function to get the correct back URL based on source page (always with projectId when valid)
  const getBackUrl = () => {
    const base = sourcePage === "pendingApproval" ? '/pms/expenses/pending-approval' : '/pms/expenses/reports';
    return withProjectId(base);
  };

  const handleCreateExpense = () => {
    const path = withProjectId('/pms/expenses/new-expense');
    // If we're in modal view, close the modal first, then navigate
    if (isModalView) {
      onBack(); // This will close the modal
      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        navigate(path, {
          state: {
            projectId: projectId,
            reportId: report?.id,
            reportName: report?.reportName,
            fromReportBreakDown: true,
            backUrl: getBackUrl(),
            refreshData: true 
          }
        });
      }, 100);
    } else {
      // Navigate directly if not in modal view
      navigate(path, {
        state: {
          projectId: projectId,
          reportId: report?.id,
          reportName: report?.reportName,
          fromReportBreakDown: true,
          backUrl: getBackUrl(),
          refreshData: true
        }
      });
    }
  };

  const handleCreateAdvance = () => {
    const path = withProjectId('/pms/expenses/new-advance');
    // If we're in modal view, close the modal first, then navigate
    if (isModalView) {
      onBack(); // This will close the modal
      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        navigate(path, {
          state: {
            projectId: projectId,
            reportId: report?.id,
            reportName: report?.reportName,
            fromReportBreakDown: true,
            backUrl: getBackUrl(),
            refreshData: true 
          }
        });
      }, 100);
    } else {
      // Navigate directly if not in modal view
      navigate(path, {
        state: {
          projectId: projectId,
          reportId: report?.id,
          reportName: report?.reportName,
          fromReportBreakDown: true,
          backUrl: getBackUrl(),
          refreshData: true
        }
      });
    }
  }; 

  // Handle view/edit expense - show detail view
  const handleViewEditExpense = async (expense: Expense, isViewMode: boolean = false) => {
    try {
      setLoading(true);
      
      // Fetch full expense data using the new API
      const response = await expensesApis.fetchPayment(expense.expenseId);
      
      if (response && response.status && response.data) {
        const fullExpenseData = response.data;
        
        // Force view mode if expense status is approved
        const isApproved = expense.paymentStatus?.toLowerCase() === 'approved';
        const finalViewMode = isApproved ? true : isViewMode;
        
        const path = withProjectId('/pms/expenses/new-expense');
        // If we're in modal view, close the modal first, then navigate
        if (isModalView) {
          onBack(); // This will close the modal
          // Use setTimeout to ensure modal closes before navigation
          setTimeout(() => {
            navigate(path, {
              state: {
                projectId: projectId,
                reportId: report?.id,
                reportName: report?.reportName,
                expenseId: expense.expenseId,
                expenseData: fullExpenseData,
                editMode: !finalViewMode,
                viewMode: finalViewMode,
                fromReportBreakDown: true,
                backUrl: getBackUrl(),
                refreshData: true,
                createdBy: expense.createdBy || getUserName(expense)
              }
            });
          }, 100);
        } else {
          // Navigate directly if not in modal view
          navigate(path, {
            state: {
              projectId: projectId,
              reportId: report?.id,
              reportName: report?.reportName,
              expenseId: expense.expenseId,
              expenseData: fullExpenseData,
              editMode: !finalViewMode,
              viewMode: finalViewMode,
              fromReportBreakDown: true,
              backUrl: getBackUrl(),
              refreshData: true,
              createdBy: expense.createdBy || getUserName(expense)
            }
          });
        }
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to fetch expense data",
          icon: "error",
          confirmButtonText: "OK"
        });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle view/edit advance - show detail view
  const handleViewEditAdvance = async (advance: Expense, isViewMode: boolean = false) => {
    try {
      setLoading(true);
      
      // Fetch full advance data using the API
      if (advance.expenseId) {
        const response = await expensesApis.fetchPayment(advance.expenseId);
        
        if (response && response.status && response.data) {
          const fullAdvanceData = response.data;
          
          const advancePath = withProjectId('/pms/expenses/new-advance');
          // If we're in modal view, close the modal first, then navigate
          if (isModalView) {
            onBack(); // This will close the modal
            // Use setTimeout to ensure modal closes before navigation
            setTimeout(() => {
              navigate(advancePath, {
                state: {
                  projectId: projectId,
                  reportId: report?.id,
                  reportName: report?.reportName,
                  advanceData: fullAdvanceData,
                  isEditMode: !isViewMode,
                  isViewMode: isViewMode,
                  fromReportBreakDown: true,
                  backUrl: getBackUrl(),
                  refreshData: true,
                  createdBy: advance.createdBy || getUserName(advance)
                }
              });
            }, 100);
          } else {
            // Navigate directly if not in modal view
            navigate(advancePath, {
              state: {
                projectId: projectId,
                reportId: report?.id,
                reportName: report?.reportName,
                advanceData: fullAdvanceData,
                isEditMode: !isViewMode,
                isViewMode: isViewMode,
                fromReportBreakDown: true,
                backUrl: getBackUrl(),
                refreshData: true,
                createdBy: advance.createdBy || getUserName(advance)
              }
            });
          }
        } else {
          Swal.fire({
            title: "Error!",
            text: "Failed to fetch advance data for editing",
            icon: "error",
            confirmButtonText: "OK"
          });
        }
      } else {
        // If no expenseId, navigate with the existing advance data
        const noExpenseAdvancePath = withProjectId('/pms/expenses/new-advance');
        if (isModalView) {
          onBack();
          setTimeout(() => {
            navigate(noExpenseAdvancePath, {
              state: {
                projectId: projectId,
                reportId: report?.id,
                reportName: report?.reportName,
                advanceData: advance,
                isEditMode: !isViewMode,
                isViewMode: isViewMode,
                fromReportBreakDown: true,
                backUrl: getBackUrl(),
                refreshData: true,
                createdBy: advance.createdBy || getUserName(advance)
              }
            });
          }, 100);
        } else {
          navigate(noExpenseAdvancePath, {
            state: {
              projectId: projectId,
              reportId: report?.id,
              reportName: report?.reportName,
              advanceData: advance,
              isEditMode: !isViewMode,
              isViewMode: isViewMode,
              fromReportBreakDown: true,
              backUrl: getBackUrl(),
              refreshData: true,
              createdBy: advance.createdBy || getUserName(advance)
            }
          });
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete advance
  const handleDeleteAdvance = async (advance: Expense) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        // Call the delete API if the advance has an expenseId
        if (advance.expenseId) {
          const response = await expensesApis.deletePaymentIn(advance.expenseId);

          if (response && response.status) {
            // Remove advance from local state
            setAdvances(prev => prev.filter(adv => adv.id !== advance.id));
            
            // Refresh data if callback is provided
            if (onRefreshComplete) {
              onRefreshComplete();
            }

            Swal.fire("Deleted!", "Payment advance has been deleted.", "success");
          } else {
            throw new Error(response?.message || "Failed to delete payment advance");
          }
        } else {
          // For local-only advances (no API call needed)
          // Remove advance from local state
          setAdvances(prev => prev.filter(adv => adv.id !== advance.id));
          
          // Refresh data if callback is provided
          if (onRefreshComplete) {
            onRefreshComplete();
          }

          Swal.fire("Deleted!", "Payment advance has been deleted.", "success");
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        Swal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK"
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle delete expense
  const handleDeleteExpense = async (expense: Expense) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#2557d6",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await expensesApis.deleteExpense(expense.expenseId);

        if (response && response.status) {
          // Remove expense from local state
          setExpenses(prev => prev.filter(exp => exp.id !== expense.id));

          Swal.fire("Deleted!", "Expense has been deleted.", "success");
        } else {
          Swal.fire({
            title: "Error!",
            text: response?.message,
            icon: "error",
            confirmButtonText: "OK"
          });
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        Swal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK"
        });
      } finally {
        setLoading(false);
      }
    }
  };


  // Document upload functions for sidebar
  const handleDocumentUpload = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      setUploadedDocuments(prev => [...prev, ...newFiles]);
    }
  };

  // Receipt upload function for expenses tab
  const handleReceiptUpload = (files: FileList) => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    fileArray.forEach(file => {
      // Check file size
      if (file.size > maxSize) {
        Swal.fire({
          title: "File Too Large",
          text: `File "${file.name}" is larger than 10MB. Please choose a smaller file.`,
          icon: "warning",
          confirmButtonText: "OK"
        });
        return;
      }

      // Check file type
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: "Invalid File Type",
          text: `File "${file.name}" is not a supported format. Please upload images, PDF, DOC, or XLS files.`,
          icon: "warning",
          confirmButtonText: "OK"
        });
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setUploadedDocuments(prev => [...prev, ...validFiles]);

      if (validFiles.length === 1) {
        Swal.fire({
          title: "Success!",
          text: "Receipt uploaded successfully",
          icon: "success",
          confirmButtonText: "OK"
        });
      } else {
        Swal.fire({
          title: "Success!",
          text: `${validFiles.length} receipts uploaded successfully`,
          icon: "success",
          confirmButtonText: "OK"
        });
      }
    }
  };

  const removeDocument = (index: number) => {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  };



  const handleBulkSendForApproval = async () => {
    if (selectedExpenses.length === 0) {
      Swal.fire({
        title: "No Selection",
        text: "Please select expenses to send for approval",
        icon: "warning",
        confirmButtonText: "OK"
      });
      return;
    }

    if (!report?.id) {
      Swal.fire({
        title: "Error",
        text: "Report ID is required",
        icon: "error",
        confirmButtonText: "OK"
      });
      return;
    }

    // Add confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to send ${selectedExpenses.length} expense(s) for approval. This action cannot be undone.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, send for approval!",
      cancelButtonText: "Cancel"
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      // Format expenseId as comma-separated string for multiselect
      const expenseIdString = selectedExpenses.join(',');
      const response = await expensesApis.createExpenseApprovals({ 
        reportId: parseInt(report.id), 
        expenseId: expenseIdString 
      });

      // Check if response indicates an error
      if (response && response.status === false) {
        // This is an error response from the API
        Swal.fire({
          title: "Error!",
          text: response.message || "Failed to send expenses for approval",
          icon: "error",
          confirmButtonText: "OK"
        });
        return;
      }

      // Check if response is an error object (from makeApiCall catch block)
      if (response && response.response && response.response.data) {
        // This is an error response
        Swal.fire({
          title: "Error!",
          text: response.response.data.message,
          icon: "error",
          confirmButtonText: "OK"
        });
        return;
      }

      const isSuccess = response && response.status === true;
      const successful = isSuccess ? selectedExpenses.length : 0;
      const failed = selectedExpenses.length - successful;

      if (successful > 0) {
        // Update all selected expenses status in local state
        setExpenses(prev => prev.map(expense =>
          selectedExpenses.includes(expense.id)
            ? { ...expense, paymentStatus: 'PENDING' }
            : expense
        ));

        setSelectedExpenses([]);

        if (failed > 0) {
          Swal.fire({
            title: "Partial Success!",
            text: `${successful} expense(s) sent for approval successfully, ${failed} failed`,
            icon: "warning",
            confirmButtonText: "OK"
          });
        } else {
          Swal.fire({
            title: "Success!",
            text: `${successful} expense(s) sent for approval successfully`,
            icon: "success",
            confirmButtonText: "OK"
          });
        }
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to send expenses for approval. Please try again.",
          icon: "error",
          confirmButtonText: "OK"
        });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseSelection = (expenseId: string, checked: boolean) => {
    if (checked) {
      setSelectedExpenses(prev => [...prev, expenseId]);
    } else {
      setSelectedExpenses(prev => prev.filter(id => id !== expenseId));
    }
  };

  // Helper function to get selectable expenses based on current context
  const getSelectableExpenses = () => {
    const currentUserId = currentUser?.id || currentUser?.userId;
    
    return expenses.filter(expense => {
      if (sourcePage === "pendingApproval") {
        // For pending approvals, select expenses where:
        // 1. Expense status is pending AND
        // 2. Current user is in approval hierarchy with pending status AND
        // 3. Current user hasn't already approved this expense
        const isExpensePending = expense.paymentStatus?.toLowerCase() === 'pending';
        const isUserInHierarchy = isCurrentUserInApprovalHierarchy(expense.expenseApprovals || []);
        
        // Check if current user has already approved this expense
        const hasUserApproved = expense.expenseApprovals?.some((approval: any) => 
          approval.userId === currentUserId && 
          (approval.status?.toLowerCase() === 'approved' || approval.status?.toLowerCase() === 'approve')
        );
        
        return isExpensePending && isUserInHierarchy && !hasUserApproved;
      } else {
        // For other pages, select draft, pending (with user in hierarchy), or rejected expenses
        return expense.paymentStatus?.toUpperCase() === 'DRAFT' || 
               (expense.paymentStatus?.toLowerCase() === 'pending' && isCurrentUserInApprovalHierarchy(expense.expenseApprovals || [])) ||
               expense.paymentStatus?.toUpperCase() === 'REJECTED';
      }
    });
  };

  const handleSelectAllExpenses = (checked: boolean) => {
    if (checked) {
      // Select all expenses that can be selected based on the current context
      const selectableExpenseIds = getSelectableExpenses().map(expense => expense.id);
      setSelectedExpenses(selectableExpenseIds);
    } else {
      setSelectedExpenses([]);
    }
  };

  // Function to fetch fresh data with updated approval information
  const fetchFreshData = async () => {
    if (!report?.id || !projectId) return;
    
    try {
      setLoading(true);
      
      // Force refresh by setting refreshData to true
      await loadInitialData(true);
      
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async (refreshData = false) => {
    if (loading || isInitialDataLoading.current) {
      return;
    }
    
    if (!report?.id || !projectId) {
      return;
    }
    
    isInitialDataLoading.current = true;
    setLoading(true);
    
    try {
      if (report?.id && projectId) {
        // Check if data is already fetched from PendingApproval and we're not forcing a refresh
        if (report.dataAlreadyFetched && report.fetchedReportData && report.fetchedExpensesData && !refreshData) {
          
          // Use the pre-fetched report data and preserve user totals and pending totals from original report
          const updatedReportData = {
            ...report.fetchedReportData,
            userTotalIn: report?.userTotalIn || report.fetchedReportData.userTotalIn,
            userTotalOut: report?.userTotalOut || report.fetchedReportData.userTotalOut,
            userBalance: report?.userBalance || report.fetchedReportData.userBalance,
            userReimbursableAmount: report?.userReimbursableAmount || report.fetchedReportData.userReimbursableAmount,
            // Preserve pending-specific fields
            pendingUserTotalIn: report?.pendingUserTotalIn || report.fetchedReportData.pendingUserTotalIn,
            pendingUserTotalOut: report?.pendingUserTotalOut || report.fetchedReportData.pendingUserTotalOut,
            pendingUserBalance: report?.pendingUserBalance || report.fetchedReportData.pendingUserBalance,
            pendingUserReimbursableAmount: report?.pendingUserReimbursableAmount || report.fetchedReportData.pendingUserReimbursableAmount,
            pendingUserNonReimbursableAmount: report?.pendingUserNonReimbursableAmount || report.fetchedReportData.pendingUserNonReimbursableAmount,
            pendingUserTaxAmount: report?.pendingUserTaxAmount || report.fetchedReportData.pendingUserTaxAmount,
            pendingReimbursableTaxAmount: report?.pendingReimbursableTaxAmount || report.fetchedReportData.pendingReimbursableTaxAmount,
            pendingUserReimbursableTaxAmount: report?.pendingUserReimbursableTaxAmount || report.fetchedReportData.pendingUserReimbursableTaxAmount
          };
          setReportData(updatedReportData);
          
          // Notify parent component of fresh data
          if (onFreshDataUpdate) {
            onFreshDataUpdate(updatedReportData);
          }
          
          // Process the pre-fetched expenses data
          const expensesList = report.fetchedExpensesData.list || [];
          
          // Check if we've reached the end
          if (expensesList.length < itemsPerPage || !expensesList) {
            setExpensesEndOfList(true);
            setAdvancesEndOfList(true);
          }

          // Update pagination ID for next request
          if (expensesList.length > 0) {
            setExpensesPaginationId(expensesList[expensesList.length - 1]?.paginationId || null);
            setAdvancesPaginationId(expensesList[expensesList.length - 1]?.paginationId || null);
          }

          // Filter expenses by the specific report ID
          const reportExpenses = expensesList.filter((expense: any) =>
            expense.expenseReportId?.toString() === report.id
          );

          // Transform API response to match Expense interface
          const transformedExpenses: Expense[] = reportExpenses.map(transformExpenseData);

          // Filter for expenses only
          const expenseItems = transformedExpenses.filter(expense =>
            expense.paymentType?.toLowerCase() === 'paymentout' ||
            expense.paymentType?.toLowerCase() === 'expense' ||
            !expense.paymentType
          );

          // Filter for advances only
          const advanceItems = transformedExpenses.filter(expense =>
            expense.paymentType?.toLowerCase() === 'paymentin' ||
            expense.paymentType?.toLowerCase() === 'advance' ||
            expense.paymentType?.toLowerCase() === 'reimbursement'
          );

          // Set both expenses and advances from pre-fetched data
          setExpenses(expenseItems);
          setAdvances(advanceItems);
          
          setLoading(false);
          isInitialDataLoading.current = false;
          return;
        }
        
        // Original API call logic for when data is not pre-fetched
        const userId = currentUser?.id || currentUser?.userId;
        
        // Prepare API parameters for initial load
        const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";
        
        // Check if we already have report data and it's not a refresh
        if (!reportData || refreshData) {
          // Use status from report data if available, otherwise use "Pending" for pending approvals
          const statusToUse = report?.extra?.status || (sourcePage === "pendingApproval" ? "Pending" : undefined);
          const reportParams: any = {};
          if (statusToUse) {
            // Use paymentStatus for pending approvals, status for others
            if (sourcePage === "pendingApproval") {
              reportParams.paymentStatus = statusToUse;
            } else {
              reportParams.status = statusToUse;
              reportParams.userId = userId; // Only add userId for non-pending approval calls
            }
          } else if (sourcePage === "reports") {
            // For reports page, always pass userId even if no status
            reportParams.userId = userId;
          }
          
          const reportResponse = await expensesApis.fetchReport(report.id, reportParams);
          if (reportResponse && reportResponse.status && reportResponse.data) {
            // Preserve user totals and pending totals from the original report data when setting new report data
            const updatedReportData = {
              ...reportResponse.data,
              // Preserve createdBy information
              createdBy: reportResponse.data.createdBy || report?.createdBy,
              createdByName: (reportResponse.data as any).createdByName || report?.createdByName,
              userTotalIn: report?.userTotalIn || reportResponse.data.userTotalIn,
              userTotalOut: report?.userTotalOut || reportResponse.data.userTotalOut,
              userBalance: report?.userBalance || reportResponse.data.userBalance,
              userReimbursableAmount: report?.userReimbursableAmount || reportResponse.data.userReimbursableAmount,
              userReimbursableBalance: report?.userReimbursableBalance || reportResponse.data.userReimbursableBalance,
              // Preserve pending-specific fields
              pendingUserTotalIn: report?.pendingUserTotalIn || reportResponse.data.pendingUserTotalIn,
              pendingUserTotalOut: report?.pendingUserTotalOut || reportResponse.data.pendingUserTotalOut,
              pendingUserBalance: report?.pendingUserBalance || reportResponse.data.pendingUserBalance,
              pendingUserReimbursableAmount: report?.pendingUserReimbursableAmount || reportResponse.data.pendingUserReimbursableAmount,
              pendingUserNonReimbursableAmount: report?.pendingUserNonReimbursableAmount || reportResponse.data.pendingUserNonReimbursableAmount,
              pendingUserTaxAmount: report?.pendingUserTaxAmount || reportResponse.data.pendingUserTaxAmount,
              pendingReimbursableTaxAmount: report?.pendingReimbursableTaxAmount || reportResponse.data.pendingReimbursableTaxAmount,
              pendingUserReimbursableTaxAmount: report?.pendingUserReimbursableTaxAmount || reportResponse.data.pendingUserReimbursableTaxAmount
            };
            setReportData(updatedReportData);
            
            // Notify parent component of fresh data
            if (onFreshDataUpdate) {
              onFreshDataUpdate(updatedReportData);
            }
          }
        }
        
        // Use status from report data if available, otherwise use "Pending" for pending approvals
        const statusToUse = report?.extra?.status || (sourcePage === "pendingApproval" ? "Pending" : undefined);
        
        const apiParams = {
          pageSize: itemsPerPage,
          pageDiff: 0,
          sortOrder: 'DESC',
          direction: 'P',
          paginationId: null,
          projectId: projectId,
          expenseReportId: report.id,
          // Only add status parameter if we have a specific status to use
          ...(statusToUse && (sourcePage === "pendingApproval" ? { paymentStatus: statusToUse } : { status: statusToUse })),
          // For non-admin users, filter by userId (only for non-pending approval calls)
          // For admin users, don't filter by userId to see all expenses
          ...(sourcePage !== "pendingApproval" && sourcePage !== "ledger" && !isAdmin && { 
            userId: userId
          }),
        };

        // Fetch expenses data once
        const response = await expensesApis.fetchExpenses(projectId, apiParams);
        
        if (response && response.status && response.data) {
          const expensesList = response.data.list || [];
          
          // Check if we've reached the end
          if (expensesList.length < apiParams.pageSize || !expensesList) {
            setExpensesEndOfList(true);
            setAdvancesEndOfList(true);
          }

          // Update pagination ID for next request
          if (expensesList.length > 0) {
            setExpensesPaginationId(expensesList[expensesList.length - 1]?.paginationId || null);
            setAdvancesPaginationId(expensesList[expensesList.length - 1]?.paginationId || null);
          }

          // Filter expenses by the specific report ID
          const reportExpenses = expensesList.filter((expense: any) =>
            expense.expenseReportId?.toString() === report.id
          );

          // Transform API response to match Expense interface
          const transformedExpenses: Expense[] = reportExpenses.map(transformExpenseData);

          // Filter for expenses only
          const expenseItems = transformedExpenses.filter(expense =>
            expense.paymentType?.toLowerCase() === 'paymentout' ||
            expense.paymentType?.toLowerCase() === 'expense' ||
            !expense.paymentType
          );

          // Filter for advances only
          const advanceItems = transformedExpenses.filter(expense =>
            expense.paymentType?.toLowerCase() === 'paymentin' ||
            expense.paymentType?.toLowerCase() === 'advance' ||
            expense.paymentType?.toLowerCase() === 'reimbursement'
          );

          // Set both expenses and advances from single API call
          setExpenses(expenseItems);
          setAdvances(advanceItems);
        } else {
          // Set empty arrays to show "no data" state
          setExpenses([]);
          setAdvances([]);
        }
      } else {
        setExpenses([]);
        setAdvances([]);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setExpenses([]);
      setAdvances([]);
    } finally {
      setLoading(false);
      isInitialDataLoading.current = false;
    }
  };

  // Lazy Loading: Load more expenses for infinite scroll
  const loadMoreExpenses = async () => {
    if (expensesPaginationId === undefined || (expensesPaginationId !== null && expensesEndOfList) || loading || isLoadingMoreExpenses.current) {
      return;
    }
    
    isLoadingMoreExpenses.current = true;
    setLoading(true);
    try {
      if (report?.id && projectId) {
        // Prepare API parameters for pagination
        const userId = currentUser?.id || currentUser?.userId;
        const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";
        // Use status from report data if available, otherwise use "Pending" for pending approvals
        const statusToUse = report?.extra?.status || (sourcePage === "pendingApproval" ? "Pending" : undefined);
        
        const apiParams = {
          pageSize: itemsPerPage,
          pageDiff: 0,
          sortOrder: 'DESC',
          direction: expensesPaginationId ? 'F' : 'P',
          paginationId: expensesPaginationId,
          projectId: projectId,
          expenseReportId: report.id,
          // Only add status parameter if we have a specific status to use
          ...(statusToUse && (sourcePage === "pendingApproval" ? { paymentStatus: statusToUse } : { status: statusToUse })),
          // For non-admin users, filter by userId in createdBy (only for non-pending approval calls)
          // For admin users, don't filter by userId to see all expenses
          ...(sourcePage !== "pendingApproval" && sourcePage !== "ledger" && !isAdmin && { 
            userId: userId,
            createdBy: report.createdBy || userId 
          }),
        };

        // Fetch expenses data
        const response = await expensesApis.fetchExpenses(projectId, apiParams);
        
        if (response && response.status && response.data) {
          const expensesList = response.data.list || [];
          
          // Check if we've reached the end
          if (expensesList.length < apiParams.pageSize || !expensesList) {
            setExpensesEndOfList(true);
          }

          // Update pagination ID for next request
          if (expensesList.length > 0) {
            setExpensesPaginationId(expensesList[expensesList.length - 1]?.paginationId || null);
          }

          // Filter expenses by the specific report ID
          const reportExpenses = expensesList.filter((expense: any) =>
            expense.expenseReportId?.toString() === report.id
          );

          // Transform API response to match Expense interface
          const transformedExpenses: Expense[] = reportExpenses.map(transformExpenseData);

          // Filter for expenses only
          const expenseItems = transformedExpenses.filter(expense =>
            expense.paymentType?.toLowerCase() === 'paymentout' ||
            expense.paymentType?.toLowerCase() === 'expense' ||
            !expense.paymentType
          );

          // Append new data to existing expenses
          if (expenseItems.length > 0) {
            setExpenses((prev: any) => [...prev, ...expenseItems]);
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
    } finally {
      setLoading(false);
      isLoadingMoreExpenses.current = false;
    }
  };

  // Lazy Loading: Load more advances for infinite scroll
  const loadMoreAdvances = async () => {
    if (advancesPaginationId === undefined || (advancesPaginationId !== null && advancesEndOfList) || loading || isLoadingMoreAdvances.current) {
      return;
    }
    
    isLoadingMoreAdvances.current = true;
    setLoading(true);
    try {
      if (report?.id && projectId) {
        // Prepare API parameters for pagination
        const userId = currentUser?.id || currentUser?.userId;
        const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";
        // Use status from report data if available, otherwise use "Pending" for pending approvals
        const statusToUse = report?.extra?.status || (sourcePage === "pendingApproval" ? "Pending" : undefined);
        
        const apiParams = {
          pageSize: itemsPerPage,
          pageDiff: 0,
          sortOrder: 'DESC',
          direction: advancesPaginationId ? 'F' : 'P',
          paginationId: advancesPaginationId,
          projectId: projectId,
          expenseReportId: report.id,
          // Only add status parameter if we have a specific status to use
          ...(statusToUse && (sourcePage === "pendingApproval" ? { paymentStatus: statusToUse } : { status: statusToUse })),
          // For non-admin users, filter by userId in createdBy (only for non-pending approval calls)
          // For admin users, don't filter by userId to see all expenses
          ...(sourcePage !== "pendingApproval" && sourcePage !== "ledger" && !isAdmin && { 
            userId: userId,
            createdBy: report.createdBy || userId 
          }),
        };

        // Fetch expenses data
        const response = await expensesApis.fetchExpenses(projectId, apiParams);
        
        if (response && response.status && response.data) {
          const expensesList = response.data.list || [];
          
          // Check if we've reached the end
          if (expensesList.length < apiParams.pageSize || !expensesList) {
            setAdvancesEndOfList(true);
          }

          // Update pagination ID for next request
          if (expensesList.length > 0) {
            setAdvancesPaginationId(expensesList[expensesList.length - 1]?.paginationId || null);
          }

          // Filter expenses by the specific report ID
          const reportExpenses = expensesList.filter((expense: any) =>
            expense.expenseReportId?.toString() === report.id
          );

          // Transform API response to match Expense interface
          const transformedExpenses: Expense[] = reportExpenses.map(transformExpenseData);

          // Filter for advances only
          const advanceItems = transformedExpenses.filter(expense =>
            expense.paymentType?.toLowerCase() === 'paymentin' ||
            expense.paymentType?.toLowerCase() === 'advance' ||
            expense.paymentType?.toLowerCase() === 'reimbursement'
          );

          // Append new data to existing advances
          if (advanceItems.length > 0) {
            setAdvances((prev: any) => [...prev, ...advanceItems]);
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
    } finally {
      setLoading(false);
      isLoadingMoreAdvances.current = false;
    }
  };


  // Calculate totals from individual expenses and report data
  // Use pending-specific totals for pending approvals, otherwise use user-specific or general totals
  const isPendingApproval = sourcePage === "pendingApproval";
  
  const totalExpenseAmount = isPendingApproval 
    ? (reportData?.pendingUserTotalOut || reportData?.userTotalOut || 0)
    : (reportData?.userTotalOut || 0);
    
  const reimbursableAmount = isPendingApproval
    ? (reportData?.pendingUserReimbursableAmount || reportData?.userReimbursableAmount || 0)
    : (reportData?.userReimbursableAmount || 0);
    
  const nonReimbursableAmount = isPendingApproval
    ? (reportData?.pendingUserNonReimbursableAmount || reportData?.userNonReimbursableAmount || 0)
    : (reportData?.userNonReimbursableAmount || 0);
  
  // Calculate advances-only values from individual advances (since we need to filter by status)
  const userTotalIn = isPendingApproval
    ? (reportData?.pendingUserTotalIn || reportData?.userTotalIn || 0)
    : (reportData?.userTotalAdvances || reportData?.userTotalIn || 0);
    
  const appliedAdvanceAmount = isPendingApproval
    ? (reportData?.pendingUserTotalIn || reportData?.userTotalIn || 0)
    : (reportData?.userTotalIn || 0);
  const pendingAdvanceAmount = userTotalIn - appliedAdvanceAmount;
  
  // Overall totals calculated from report data and individual expenses
  // Use pending-specific totals for pending approvals, otherwise use user-specific or general totals
  const overallTotalIn = isPendingApproval
    ? (reportData?.pendingUserTotalIn || reportData?.userTotalIn || 0)
    : (reportData?.userTotalIn || 0);
    
  const overallTotalOut = isPendingApproval
    ? (reportData?.pendingUserTotalOut || reportData?.userTotalOut || 0)
    : (reportData?.userTotalOut || 0);
    
  const overallBalance = isPendingApproval
    ? (reportData?.pendingUserBalance || reportData?.userBalance || 0)
    : (reportData?.userBalance || 0);
    
  const overallReimbursableAmount = isPendingApproval
    ? (reportData?.pendingUserReimbursableAmount || reportData?.userReimbursableAmount || 0)
    : (reportData?.userReimbursableAmount || 0);
    
  const overallNonReimbursableAmount = isPendingApproval
    ? (reportData?.pendingUserNonReimbursableAmount || reportData?.userNonReimbursableAmount || 0)
    : (reportData?.userNonReimbursableAmount || 0);
    
  const overallTaxAmount = isPendingApproval
    ? (reportData?.pendingUserTaxAmount || reportData?.userReimbursableTaxAmount || 0)
    : (reportData?.userReimbursableTaxAmount || 0);

  // Approval/Rejection functions
  const handleApproveReport = async () => {
    if (!report?.id) return;
    
    Swal.fire({
      title: "Are you sure you want to approve?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsApproving(true);
          const response = await expensesApis.approveReport({ reportId: parseInt(report.id) });
          
          // Check for success in multiple possible response structures
          const isSuccess = response?.status === true || 
                           response?.data?.status === true || 
                           response?.status === 200 ||
                           (response?.data && !response?.error);
          
          if (isSuccess) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: response?.data?.message || response?.message || "Report approved successfully",
              confirmButtonText: "Ok",
            });
            
            // Data will be refreshed by fetchFreshData() call below
            
            // Removed custom event dispatch to prevent multiple API calls
            
            // Refresh the data with fresh approval information
            await fetchFreshData();
            if (onRefreshComplete) {
              onRefreshComplete();
            }
            // Clear selected expenses since they are now approved
            setSelectedExpenses([]);
            
            // Dispatch custom event to notify parent components about the approval immediately
            window.dispatchEvent(new CustomEvent('reportBreakDownClosed', { 
              detail: { action: 'approved', reportId: report.id } 
            }));
            
            // Show updated data for a moment before closing modal
            setTimeout(() => {
              onBack();
            }, 1000);
          } else {
            const errorMessage = response?.data?.message || 
                               response?.message || 
                               response?.error?.message ||
                               "Failed to approve report. Please try again.";
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
              confirmButtonText: "OK"
            });
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK"
          });
        } finally {
          setIsApproving(false);
        }
      }
    });
  };

  const handleRejectReport = async () => {
    if (!report?.id || !rejectReason.trim()) return;
    
    Swal.fire({
      title: "Are you sure you want to reject?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reject it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsRejecting(true);
          const response = await expensesApis.rejectReport({ 
            reportId: parseInt(report.id), 
            reason: rejectReason.trim() 
          });
          // Check for success in multiple possible response structures
          const isSuccess = response?.status === true || 
                           response?.data?.status === true || 
                           response?.status === 200 ||
                           (response?.data && !response?.error);
          
          if (isSuccess) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: response?.data?.message || response?.message || "Report rejected successfully",
              confirmButtonText: "Ok",
            });
            
            // Data will be refreshed by fetchFreshData() call below
            
            // Removed custom event dispatch to prevent multiple API calls
            
            // Refresh the data with fresh approval information
            await fetchFreshData();
            if (onRefreshComplete) {
              onRefreshComplete();
            }
            
            // Dispatch custom event to notify parent components about the rejection immediately
            window.dispatchEvent(new CustomEvent('reportBreakDownClosed', { 
              detail: { action: 'rejected', reportId: report.id } 
            }));
            
            // Show updated data for a moment before closing modal
            setTimeout(() => {
              onBack();
            }, 1000);
          } else {
            const errorMessage = response?.data?.message || 
                               response?.message || 
                               response?.error?.message ||
                               "Failed to reject report. Please try again.";
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
              confirmButtonText: "OK"
            });
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK"
          });
        } finally {
          setIsRejecting(false);
          setIsRejectModalOpen(false);
          setRejectReason('');
        }
      }
    });
  };

  // Bulk approval/rejection functions for individual expenses
  const handleBulkApproveExpenses = async () => {
    if (selectedExpenses.length === 0) {
      Swal.fire({
        title: "No Selection",
        text: "Please select expenses to approve",
        icon: "warning",
        confirmButtonText: "OK"
      });
      return;
    }

    Swal.fire({
      title: "Are you sure you want to approve?",
      text: `You are about to approve ${selectedExpenses.length} expense(s). You won't be able to revert this!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve them!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          
          // Collect all expense approval IDs for bulk approval
          const expenseApprovalIds: number[] = [];
          const currentUserId = currentUser?.id || currentUser?.userId;
          
          selectedExpenses.forEach(expenseId => {
            const expense = expenses.find(e => e.id === expenseId);
            if (expense && expense.expenseApprovals && expense.expenseApprovals.length > 0) {
              // Find the current user's approval record
              const userApproval = expense.expenseApprovals.find((approval: any) => 
                approval.userId === currentUserId && approval.status === 'Pending'
              );
              if (userApproval && userApproval.expenseApprovalId) {
                expenseApprovalIds.push(userApproval.expenseApprovalId);
              }
            }
          });

          if (expenseApprovalIds.length === 0) {
            Swal.fire({
              title: "Error!",
              text: "No valid approvals found for selected expenses",
              icon: "error",
              confirmButtonText: "OK"
            });
            return;
          }

          // Make single API call with comma-separated expense approval IDs
          const response = await expensesApis.bulkApproveExpenses({ 
            expenseApprovalId: expenseApprovalIds.join(',') 
          });

          if (response && response.status) {
            setSelectedExpenses([]);
            Swal.fire({
              icon: "success",
              title: "Success",
              text: `${expenseApprovalIds.length} expense(s) approved successfully`,
              confirmButtonText: "Ok",
            });
            
            // Data will be refreshed by fetchFreshData() call below
            
            // Removed custom event dispatch to prevent multiple API calls
            
            // Refresh the data with fresh approval information
            await fetchFreshData();
            // Notify parent components to refresh their data
            if (onRefreshComplete) {
              onRefreshComplete();
            }
            
            // Dispatch custom event to notify parent components about bulk approval immediately
            window.dispatchEvent(new CustomEvent('reportBreakDownClosed', { 
              detail: { action: 'bulkApproved', reportId: report?.id, count: expenseApprovalIds.length } 
            }));
          } else {
            const errorMessage = response?.data?.message || 
                               response?.message || 
                               response?.error?.message ||
                               "Failed to approve expenses. Please try again.";
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
              confirmButtonText: "OK"
            });
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK"
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleBulkRejectExpenses = async () => {
    if (selectedExpenses.length === 0 || !bulkRejectReason.trim()) return;

    Swal.fire({
      title: "Are you sure you want to reject?",
      text: `You are about to reject ${selectedExpenses.length} expense(s). You won't be able to revert this!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reject them!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const results = await Promise.allSettled(
            selectedExpenses.map(expenseId => {
              const expense = expenses.find(e => e.id === expenseId);
              if (expense && expense.expenseApprovals && expense.expenseApprovals.length > 0) {
                // Find the current user's approval record
                const currentUserId = currentUser?.id || currentUser?.userId;
                const userApproval = expense.expenseApprovals.find((approval: any) => 
                  approval.userId === currentUserId && approval.status === 'Pending'
                );
                if (userApproval && userApproval.expenseApprovalId) {
                  return expensesApis.rejectExpense({ 
                    expenseApprovalId: userApproval.expenseApprovalId, 
                    reason: bulkRejectReason.trim() 
                  });
                }
              }
              return Promise.resolve({ status: false, message: "Approval not found" });
            })
          );

          const successful = results.filter(result => 
            result.status === 'fulfilled' && result.value?.status === true
          ).length;

          const failed = results.length - successful;

          if (successful > 0) {
            setSelectedExpenses([]);
            if (failed > 0) {
              Swal.fire({
                title: "Partial Success!",
                text: `${successful} expense(s) rejected successfully, ${failed} failed`,
                icon: "warning",
                confirmButtonText: "OK"
              });
            } else {
              Swal.fire({
                icon: "success",
                title: "Success",
                text: `${successful} expense(s) rejected successfully`,
                confirmButtonText: "Ok",
              });
            }
            
            // Data will be refreshed by fetchFreshData() call below
            
            // Removed custom event dispatch to prevent multiple API calls
            
            // Refresh the data with fresh approval information
            await fetchFreshData();
            // Notify parent components to refresh their data
            if (onRefreshComplete) {
              onRefreshComplete();
            }
            
            // Dispatch custom event to notify parent components about bulk rejection immediately
            window.dispatchEvent(new CustomEvent('reportBreakDownClosed', { 
              detail: { action: 'bulkRejected', reportId: report?.id, count: successful } 
            }));
          } else {
            const errorMessage = "Failed to reject expenses. Please try again.";
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
              confirmButtonText: "OK"
            });
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK"
          });
        } finally {
          setLoading(false);
          setIsBulkRejectModalOpen(false);
          setBulkRejectReason('');
        }
      }
    });
  };

  // Inline Editing: Update payment functions for expenses and advances
  const updatePayment = async (expenseId: number, dirtyData: any, projectId: number, reportId: number, type: 'in' | 'out') => {
    try {
      setLoading(true);
      
      const payload = {
        expenseId: expenseId,
        projectId: projectId,
        reportId: reportId,
        ...dirtyData
      };

      const response = type === 'in' 
        ? await expensesApis.updatePaymentIn(payload)
        : await expensesApis.updatePaymentOut(payload);
      
      if (response && response.status) {
        Swal.fire({
          title: "Success!",
          text: `Payment ${type === 'in' ? 'In' : 'Out'} updated successfully`,
          icon: "success",
          confirmButtonText: "OK"
        });
        loadInitialData();
      } else {
        throw new Error(response?.message || `Failed to update Payment ${type === 'in' ? 'In' : 'Out'}`);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateExpenseReport = async (reportId: number, dirtyData: any, projectId: number) => {
    try {
      setLoading(true);
      
      const payload = {
        reportId: reportId,
        projectId: projectId,
        ...dirtyData
      };

      const response = await expensesApis.updateExpenseReport(payload);
      
      if (response && response.status) {
        Swal.fire({
          title: "Success!",
          text: "Expense Report updated successfully",
          icon: "success",
          confirmButtonText: "OK"
        });
        // Refresh the data
        loadInitialData();
      } else {
        throw new Error(response?.message || "Failed to update Expense Report");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  // Consolidated approve/reject buttons that work for both individual and selected expenses
  const approvalButtonsJSX = (sourcePage === "pendingApproval" && allowEdit) ? (
    <>
      <li>
        <Button
          style={{
            backgroundColor: "#d1f5ec",
            color: "#107154",
            borderColor: "#d1f5ec",
            marginRight: "8px",
            borderRadius: "4px",
            height: "32px",
            fontSize: "12px",
            padding: "4px 12px",
            fontWeight: "500"
          }}
          onClick={selectedExpenses.length > 0 ? handleBulkApproveExpenses : handleApproveReport}
          disabled={isApproving || isRejecting}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = "#38d39f";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.borderColor = "#38d39f";
            }
          }}
          onMouseLeave={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = "#d1f5ec";
              e.currentTarget.style.color = "#107154";
              e.currentTarget.style.borderColor = "#d1f5ec";
            }
          }}
        >
          {isApproving ? "Approving..." : (selectedExpenses.length > 0 ? "Approve Selected" : "Approve")}
        </Button>
      </li>
      <li>
        <Button
          style={{
            backgroundColor: "#fbe3e4",
            color: "#a94442",
            borderColor: "#fbe3e4",
            marginRight: "8px",
            borderRadius: "4px",
            height: "32px",
            fontSize: "12px",
            padding: "4px 12px",
            fontWeight: "500"
          }}
          onClick={selectedExpenses.length > 0 ? () => setIsBulkRejectModalOpen(true) : () => setIsRejectModalOpen(true)}
          disabled={isApproving || isRejecting}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = "#e74c3c";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.borderColor = "#e74c3c";
            }
          }}
          onMouseLeave={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = "#fbe3e4";
              e.currentTarget.style.color = "#a94442";
              e.currentTarget.style.borderColor = "#fbe3e4";
            }
          }}
        >
          {isRejecting ? "Rejecting..." : (selectedExpenses.length > 0 ? "Reject Selected" : "Reject")}
        </Button>
      </li>
    </>
  ) : null;

  const topRightJSX = selectedExpenses.length > 0 ? (
    <>
      {/* Bulk Send for Approval Button - show for draft and rejected expenses (non-pending approval pages) */}
      {sourcePage !== "pendingApproval" && expenses.some(expense => selectedExpenses.includes(expense.id) && (expense.paymentStatus?.toUpperCase() === 'DRAFT' || expense.paymentStatus?.toUpperCase() === 'REJECTED')) && (
        <li>
          <Button
            style={{
              backgroundColor: "#e6f7ff",
              color: "#1890ff",
              borderColor: "#e6f7ff",
              marginRight: "8px",
              borderRadius: "4px",
              height: "32px",
              fontSize: "12px",
              padding: "4px 12px",
              fontWeight: "500"
            }}
            onClick={handleBulkSendForApproval}
            disabled={loading}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#1890ff";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.borderColor = "#1890ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#e6f7ff";
              e.currentTarget.style.color = "#1890ff";
              e.currentTarget.style.borderColor = "#e6f7ff";
            }}
          >
            {loading ? "Sending..." : "Send for Approval"}
          </Button>
        </li>
      )}
    </>
  ) : null;

  // Handle back from detail view
  const handleBackFromDetailView = () => {
    setShowDetailView(false);
    setSelectedExpenseForDetail(null);
    setIsDetailViewMode('view');
  };

  // If detail view is shown, render ExpenseDetailView
  if (showDetailView && selectedExpenseForDetail) {
    const header = {
      title: isDetailViewMode === 'view' ? 'View Expense' : 'Edit Expense',
      previousPageLink: '#',
      previousPageTitle: 'Back',
      previousPageIcon: 'arrow-left',
      onBackClick: handleBackFromDetailView
    };

    const body = {
      tabOptions: {
        tab: 'details',
        setTab: () => {},
        tabs: [
          {
            label: 'Details',
            tabKey: 'details',
            content: (
              <div className="p-4">
                <h5>Expense Details</h5>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Category:</strong> {selectedExpenseForDetail.category}</p>
                    <p><strong>Amount:</strong> ₹{selectedExpenseForDetail.amount.toLocaleString()}</p>
                    <p><strong>Date:</strong> {selectedExpenseForDetail.date}</p>
                    <p><strong>Status:</strong> {selectedExpenseForDetail.paymentStatus}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Description:</strong> {selectedExpenseForDetail.description}</p>
                    <p><strong>Merchant:</strong> {selectedExpenseForDetail.merchant || 'N/A'}</p>
                    <p><strong>Payment Mode:</strong> {selectedExpenseForDetail.paymentMode || 'N/A'}</p>
                    <p><strong>Created By:</strong> {selectedExpenseForDetail.createdBy}</p>
                  </div>
                </div>
                {selectedExpenseForDetail.items && selectedExpenseForDetail.items.length > 0 && (
                  <div className="mt-3">
                    <h6>Items:</h6>
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Tax %</th>
                            <th>Amount w/o Tax</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedExpenseForDetail.items.map((item: any, index: number) => (
                            <tr key={index}>
                              <td>{item.description}</td>
                              <td>₹{item.amount.toLocaleString()}</td>
                              <td>{item.taxPercent}%</td>
                              <td>₹{item.amountWithoutTax.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="mt-3 d-flex gap-2">
                  <Button 
                    color="primary" 
                    onClick={handleBackFromDetailView}
                  >
                    Back
                  </Button>
                  {isDetailViewMode === 'view' && selectedExpenseForDetail.paymentStatus?.toLowerCase() !== 'approved' && (
                    <Button 
                      color="secondary"
                      onClick={() => {
                        setIsDetailViewMode('edit');
                        // Here you can add edit functionality
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            )
          }
        ]
      }
    };

    return (
      <div>
        {/* ExpenseDetailView component would go here */}
        <div className="expense-detail-view">
          <h4>{header.title}</h4>
          <div className="expense-detail-content">
            {body.tabOptions.tabs[0].content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <form>
          {!isModalView && (
            <div
              className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
              style={{ minHeight: "40px", border: "0px" }}
            >
              <h5 style={{ marginBottom: "10px" }}>Expense Header Details</h5>
              <div className="toggle-wrap nk-block-tools-toggle">
                <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                  <li className="pt-0 pb-0">
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      onClick={onBack}
                    >
                      <Icon name="arrow-left" />
                      <span>Back</span>
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Main Content - Removed Card wrapper for better layout */}
          <div style={{ padding: isModalView ? '10px 0' : '20px 0' }}>
            {/* Header Section with Report Name and Add Button */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-3">
                <h4 className="mb-0 fw-bold" style={{ fontSize: '14px' }}>
                  {reportData?.reportName || report?.reportName || 'Ganapati Festival Expenses'}
                </h4>
                <Tag 
                  color={
                    (reportData?.userReportStatus || reportData?.reportStatus || report?.status || "DRAFT").includes("Draft") ? "geekblue" :
                    (reportData?.userReportStatus || reportData?.reportStatus || report?.status || "DRAFT").includes("Pending") ? "gold" :
                    (reportData?.userReportStatus || reportData?.reportStatus || report?.status || "DRAFT").includes("Approved") ? "green" : 
                    (reportData?.userReportStatus || reportData?.reportStatus || report?.status || "DRAFT").includes("Rejected") ? "red" : "default"
                  } 
                  style={{ fontSize: 14, padding: '2px 12px' }}
                >
                  {reportData?.userReportStatus || reportData?.reportStatus || report?.status || "DRAFT"}
                </Tag>
                {/* Approval Hierarchy - Inline with Status Tag */}
                {(reportData?.expenseApprovalHierarchy || report?.approvalData) && ((reportData?.expenseApprovalHierarchy?.length || 0) > 0 || (report?.approvalData?.length || 0) > 0) && (
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-muted" style={{ fontSize: '12px' }}>
                      Approval:
                    </span>
                    <ApprovalTimelineContent approvalsData={reportData?.expenseApprovalHierarchy || report?.approvalData} />
                  </div>
                )}
              </div>              

              {/* Bulk Action Buttons and Add Button in Header */}
              <div className="d-flex align-items-center gap-2">
                {/* Approval buttons - show when coming from pending approvals */}
                {approvalButtonsJSX}
                {/* Bulk action buttons */}
                {topRightJSX} 
                {/* Add Button - hide when coming from pending approvals */}
                {!(sourcePage === "pendingApproval" && allowEdit) && (
                  <Button
                    className="toggle"
                    color="primary"
                    size="sm"
                    onClick={() => {
                      if (activeTab === 'advances') {
                        handleCreateAdvance();
                      } else {
                        handleCreateExpense();
                      }
                    }}
                    style={{ borderRadius: '4px', height: '28px', fontSize: '11px', padding: '2px 10px' }}
                  >
                    <Icon name={"plus"} />
                    <span>{activeTab === 'advances' ? 'New Advance' : 'New Expense'}</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Duration Info */}
            <div className="mb-2">
              <small className="text-muted">
                Duration: {reportData?.startDate && reportData?.endDate ? 
                  `${dayjs(reportData.startDate).format('DD-MMM-YYYY')} - ${dayjs(reportData.endDate).format('DD-MMM-YYYY')}` : 
                  report?.dateRange || `${dayjs().format('DD-MMM-YYYY')} - ${dayjs().format('DD-MMM-YYYY')}`}
              </small>
              {/* Reject Reason Display for Report Level */}
              {(reportData?.expenseApprovalHierarchy || report?.approvalData) && 
               (reportData?.expenseApprovalHierarchy || report?.approvalData)?.some((approval: ApprovalData) => 
                 approval.status === 'Rejected' && approval.reason
               ) && (
                <div style={{ marginTop: '4px' }}>
                  <small style={{ color: '#ff4d4f', fontWeight: '500' }}>
                    Rejected: {(reportData?.expenseApprovalHierarchy || report?.approvalData)?.find((approval: ApprovalData) => 
                      approval.status === 'Rejected' && approval.reason
                    )?.reason}
                  </small>
                </div>
              )}
            </div>
            {/* Tabs Navigation */}
            <Nav tabs className="border-bottom-0 mb-3">
              <NavItem>
                <NavLink
                  className={`${activeTab === 'expenses' ? 'active' : ''}`}
                  onClick={() => setActiveTab('expenses')}
                  style={{
                    cursor: 'pointer',
                    borderBottom: 'none',
                    border: 'none',
                  }}
                >
                  Expenses ({expenses.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`${activeTab === 'advances' ? 'active' : ''}`}
                  onClick={() => setActiveTab('advances')}
                  style={{
                    cursor: 'pointer',
                    borderBottom: 'none',
                    border: 'none',
                  }}
                >
                  Advances & Refunds ({advances.length})
                </NavLink>
              </NavItem>
            </Nav>

            {/* Tab Content */}
            <TabContent activeTab={activeTab} style={{ height: 'calc(100% - 120px)', overflow: 'hidden' }}>
              {/* Expenses Tab */}
              <TabPane tabId="expenses" style={{ height: '100%' }}>
                <div className="row g-3" style={{ height: '100%', margin: 0 }}>
                  {/* Left Panel - Expense Items */}
                  <div className="col-lg-8 col-md-7" style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0, // Important for flex children to respect overflow
                    paddingLeft: '0px',
                    paddingRight: '8px'
                  }}>
                    {/* Receipt Upload Area */}
                    <div
                      className="border border-2 border-dashed rounded p-3 mb-3"
                      style={{
                        borderColor: isDragOver ? '#1890ff' : '#d1d5db',
                        backgroundColor: isDragOver ? '#f0f8ff' : '#f9fafb',
                        minHeight: '80px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        flexShrink: 0 // Prevent this from shrinking
                      }}
                      onMouseEnter={(e) => {
                        if (!isDragOver) {
                          e.currentTarget.style.borderColor = '#1890ff';
                          e.currentTarget.style.backgroundColor = '#f0f8ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isDragOver) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onClick={() => document.getElementById('expense-receipt-upload')?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const files = e.dataTransfer.files;
                        if (files.length > 0) {
                          handleReceiptUpload(files);
                        }
                      }}
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                        style={{ display: 'none' }}
                        id="expense-receipt-upload"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleReceiptUpload(e.target.files);
                          }
                        }}
                      />
                      <div className="mb-1">
                        <Icon name="upload" size="20" className="text-muted" />
                      </div>
                      <h6 className="mb-0 text-dark fw-normal" style={{ fontSize: '14px' }}>
                        {isDragOver ? 'Drop files here' : 'Drag & drop receipts or click here to upload'}
                      </h6>
                      <small className="text-muted" style={{ fontSize: '12px' }}>
                        Supports: Images, PDF, DOC, XLS (Max 10MB per file)
                      </small>
                    </div>

                    {/* Uploaded Receipts Preview */}
                    {uploadedDocuments.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                            Uploaded Receipts ({uploadedDocuments.length})
                          </h6>
                          <Button
                            color="link"
                            size="sm"
                            onClick={() => setUploadedDocuments([])}
                            style={{ fontSize: '12px', padding: '2px 8px' }}
                          >
                            Clear All
                          </Button>
                        </div>
                        <div className="row g-2">
                          {uploadedDocuments.map((file, index) => (
                            <div key={index} className="col-md-4 col-sm-6">
                              <div className="d-flex align-items-center p-2 border rounded" style={{ backgroundColor: '#fff' }}>
                                <div className="me-2">
                                  <Icon
                                    name={file.type.startsWith('image/') ? 'image' : 'file-text'}
                                    className="text-primary"
                                    size="sm"
                                  />
                                </div>
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <div className="text-truncate" style={{ fontSize: '12px', fontWeight: '500' }}>
                                    {file.name}
                                  </div>
                                  <div className="text-muted" style={{ fontSize: '10px' }}>
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm p-1"
                                  style={{ 
                                    width: '20px', 
                                    height: '20px', 
                                    fontSize: '10px',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#dc3545'
                                  }}
                                  onClick={() => removeDocument(index)}
                                >
                                  <Icon name="trash" size="sm" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Select All Checkbox */}
                    {expenses.length > 0 && getSelectableExpenses().length > 0 && (
                      <div className="mb-3 p-2" style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="selectAllExpenses"
                            checked={selectedExpenses.length === getSelectableExpenses().length && getSelectableExpenses().length > 0}
                            onChange={(e) => handleSelectAllExpenses(e.target.checked)}
                            style={{ transform: "scale(1.1)" }}
                          />
                          <label className="form-check-label fw-bold" htmlFor="selectAllExpenses" style={{ fontSize: '14px', color: '#495057' }}>
                            Select All Expenses ({getSelectableExpenses().length} {sourcePage === "pendingApproval" ? "pending approvals" : "selectable expenses"})
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Expense Items Container with Scroll */}
                    <div style={{
                      flex: 1,
                      overflowY: 'auto',
                      minHeight: 0,
                      maxHeight: 'calc(100vh - 400px)', // Adjusted for better scrolling
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#c1c1c1 #f1f1f1',
                      paddingRight: '8px'
                    }} className="custom-scrollbar">
                      {loading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "400px",
                          }}
                        >
                          <Spin size="large" tip="Loading expenses..." />
                        </div>
                      ) : expenses.length > 0 ? (
                        <div>
                          {expenses.map((expense) => (
                            <Card key={expense.id} className="mb-3" style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "8px",
                              padding: "16px",
                              border: "1px solid #e9ecef",
                              minHeight: "120px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}>
                              <div className="d-flex align-items-start gap-3">

                                {/* Checkbox - Show for expenses that can be selected based on current context */}
                                {((sourcePage === "pendingApproval" && 
                                   expense.paymentStatus?.toLowerCase() === 'pending' && 
                                   isCurrentUserInApprovalHierarchy(expense.expenseApprovals || [])) ||
                                  (sourcePage !== "pendingApproval" && 
                                   (expense.paymentStatus?.toUpperCase() === 'DRAFT' || 
                                    (expense.paymentStatus?.toLowerCase() === 'pending' && isCurrentUserInApprovalHierarchy(expense.expenseApprovals || [])) ||
                                    expense.paymentStatus?.toUpperCase() === 'REJECTED'))) && (
                                  <input
                                    type="checkbox"
                                    className="form-check-input mt-2"
                                    style={{ transform: "scale(1.2)" }}
                                    checked={selectedExpenses.includes(expense.id)}
                                    onChange={(e) => handleExpenseSelection(expense.id, e.target.checked)}
                                    disabled={isCheckboxDisabled(expense)}
                                  />
                                )}

                                {/* Expense Details */}
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center justify-content-between mb-2">

                                    {/* Left Section - Category & Badge */}
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '2px',
                                          fontSize: '9px',
                                          padding: '3px 6px',
                                          fontWeight: '600',
                                          borderRadius: '4px',
                                          backgroundColor: '#e6f7ff',
                                          color: '#1890ff',
                                          border: '1px solid #91d5ff',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px'
                                        }}
                                      >
                                        EXP
                                      </span>
                                      <span className="text-primary fw-bold" style={{ fontSize: "15px" }}>
                                        {expense.productCategory}
                                      </span>
                                      {expense.merchant && (
                                        <span className="text-muted" style={{ fontSize: "12px" }}>
                                          • {expense.merchant}
                                        </span>
                                      )}
                                    </div>

                                    {/* Right Section - Amount & Actions */}
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="fw-bold text-dark" style={{
                                        fontSize: "14px",
                                        color: "#2c3e50",
                                      }}>
                                        ₹{expense.amount.toLocaleString()}.00
                                      </div>
                                      {expense.taxAmount && expense.taxAmount > 0 && (
                                        <div className="text-muted" style={{
                                          fontSize: "12px",
                                          color: "#6c757d",
                                        }}>
                                          Tax: ₹{expense.taxAmount.toLocaleString()}.00
                                        </div>
                                      )}
                                      {expense.reimbursableAmount && expense.reimbursableAmount > 0 && (
                                        <div className="text-muted" style={{
                                          fontSize: "12px",
                                          color: "#6c757d",
                                        }}>
                                          Reimbursable: ₹{expense.reimbursableAmount.toLocaleString()}.00
                                        </div>
                                      )}
                                      <UncontrolledDropdown>
                                        <DropdownToggle
                                          tag="button"
                                          className="btn btn-link btn-sm p-1"
                                          style={{
                                            fontSize: "14px",
                                            color: "#6c757d"
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                          }}
                                        >
                                          <Icon name="more-h" size="sm" />
                                        </DropdownToggle>
                                        <DropdownMenu end>
                                          <ul className="link-list-opt no-bdr">
                                            <li>
                                              <DropdownItem
                                                tag="a"
                                                href="#view"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  handleViewEditExpense(expense, true);
                                                }}
                                                style={{ padding: '6px 12px', fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}
                                              >
                                                <Icon name="eye" style={{ fontSize: '12px' }}></Icon>
                                                <span>View</span>
                                              </DropdownItem>
                                            </li>
                                            {expense.paymentStatus?.toLowerCase() !== 'approved' && (
                                              <li>
                                                <DropdownItem
                                                  tag="a"
                                                  href="#edit"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    handleViewEditExpense(expense, false);
                                                  }}
                                                  style={{ padding: '6px 12px', fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}
                                                >
                                                  <Icon name="edit" style={{ fontSize: '12px' }}></Icon>
                                                  <span>Edit</span>
                                                </DropdownItem>
                                              </li>
                                            )}
                                            {expense.paymentStatus?.toLowerCase() !== 'approved' && (
                                              <li>
                                                <DropdownItem
                                                  tag="a"
                                                  className='text-danger'
                                                  href="#delete"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    handleDeleteExpense(expense);
                                                  }}
                                                  style={{ padding: '6px 12px', fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}
                                                >
                                                  <Icon name="trash" style={{ fontSize: '12px' }}></Icon>
                                                  <span>Delete</span>
                                                </DropdownItem>
                                              </li>
                                            )}
                                          </ul>
                                        </DropdownMenu>
                                      </UncontrolledDropdown>
                                    </div>
                                  </div>

                                  {/* Date and Payment Info */}
                                  <div className="d-flex align-items-center gap-3 mb-2">
                                    <div className="text-muted" style={{
                                      fontSize: "13px",
                                      fontWeight: "500",
                                      letterSpacing: "0.3px"
                                    }}>
                                      {expense.date}
                                    </div>
                                    {expense.paymentMode && (
                                      <Badge color="outline-secondary" style={{ fontSize: "10px" }}>
                                        {expense.paymentMode}
                                      </Badge>
                                    )}
                                    {expense.paymentStatus && (
                                      <Tag 
                                        color={
                                          expense.paymentStatus.toLowerCase().includes("draft") ? "geekblue" :
                                          expense.paymentStatus.toLowerCase().includes("pending") ? "gold" :
                                          expense.paymentStatus.toLowerCase().includes("approved") ? "green" : 
                                          expense.paymentStatus.toLowerCase().includes("rejected") ? "red" : "default"
                                        } 
                                        style={{ fontSize: 14, padding: '2px 12px' }}
                                      >
                                        {expense.paymentStatus.charAt(0).toUpperCase() + expense.paymentStatus.slice(1).toLowerCase()}
                                      </Tag>
                                    )}
                                    {expense.expenseApprovals && expense.expenseApprovals.length > 0 && (
                                      <div className="d-flex align-items-center gap-1">
                                        <span className="text-muted" style={{ fontSize: '12px' }}>
                                          Approval:
                                        </span>
                                        <ExpenseApprovalTooltip expenseApprovals={expense.expenseApprovals} />
                                      </div>
                                    )}
                                  </div>


                                  {/* Additional Details */}
                                  <div className="d-flex align-items-center gap-3 text-muted" style={{ fontSize: "12px" }}>
                                    {expense.taxType && (
                                      <span>Tax: {expense.taxType}</span>
                                    )}
                                    {expense.referenceId && (
                                      <span>Ref: {expense.referenceId}</span>
                                    )}
                                    {expense.items && expense.items.length > 0 && (
                                      <span>{expense.items.length} item{expense.items.length !== 1 ? 's' : ''}</span>
                                    )}
                                    {expense.createdBy && (
                                      <span>Created by: {expense.createdBy}</span>
                                    )}
                                    {/* Reject Reason Display */}
                                    {expense.expenseApprovals && expense.expenseApprovals.some((approval: ApprovalData) => 
                                      approval.status === 'Rejected' && approval.reason
                                    ) && (
                                      <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
                                        Rejected: {expense.expenseApprovals.find((approval: ApprovalData) => 
                                          approval.status === 'Rejected' && approval.reason
                                        )?.reason}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                          
                          {/* Intersection Observer Target for Expenses */}
                          {!expensesEndOfList && expenses.length >= itemsPerPage && (
                            <div ref={expensesLastEntryRef} style={{ height: '20px' }} />
                          )}
                        </div>
                      ) : (
                        <div style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "200px",
                          backgroundColor: "white",
                        }}>
                          <Empty description="No Data Found" />
                        </div>
                      )}
                    </div>

                    {/* Summary Section - Fixed at bottom */}
                    <div
                      className="mt-3 pt-2"
                      style={{
                        flexShrink: 0,
                        ...(isModalView ? { paddingBottom: "24px", marginBottom: "8px" } : {}),
                      }}
                    >
                      <div className="row">
                        <div className="col-md-6 text-end">
                          <div className="mb-1" style={{ fontSize: '12px' }}>
                            <strong>{isPendingApproval ? 'Pending Total Expense Amount' : 'Total Expense Amount'}</strong>
                          </div>
                          <div className="mb-1" style={{ fontSize: '12px' }}>
                            {isPendingApproval ? 'Pending Non-reimbursable Amount' : 'Non-reimbursable Amount'}
                          </div>
                          <div className="mb-1" style={{ fontSize: '12px' }}>
                            {isPendingApproval ? 'Pending Applied Advance Amount' : 'Applied Advance Amount'}
                          </div>
                          <div className="border-top pt-1" style={{ fontSize: '12px' }}>
                            <strong>{isPendingApproval ? 'Pending Amount to be Reimbursed' : 'Amount to be Reimbursed'}</strong>
                          </div>
                        </div>
                        <div className="col-md-6 text-end">
                          <div className="mb-1" style={{ fontSize: '12px' }}>
                            <strong>₹{isPendingApproval 
                              ? (reportData?.userPendingTotalOut?.toLocaleString() || '0')
                              : (reportData?.userTotalOut?.toLocaleString() || '0')
                            }.00</strong>
                          </div>
                          <div className="mb-1" style={{ fontSize: '12px' }}>
                            ₹{isPendingApproval 
                              ? (reportData?.userPendingNonReimbursableAmount?.toLocaleString() || '0')
                              : (reportData?.userNonReimbursableAmount?.toLocaleString() || '0')
                            }.00
                          </div>
                          <div className="mb-1" style={{ fontSize: '12px' }}>
                            ₹{isPendingApproval 
                              ? (reportData?.userUnclaimedAdvanceAmount?.toLocaleString() || '0')
                              : (reportData?.userTotalIn?.toLocaleString() || '0')
                            }.00
                          </div>
                          <div className="border-top pt-1" style={{ fontSize: '12px' }}>
                            <strong>₹{isPendingApproval 
                              ? (Math.max(0, reportData?.userPendingReimbursableBalance || 0).toLocaleString())
                              : (Math.max(0, reportData?.userReimbursableBalance || 0).toLocaleString())
                            }.00</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - Summary and Actions */}
                  <div className="col-lg-4 col-md-5" style={{
                    paddingLeft: '8px',
                    paddingRight: '0px'
                  }}>
                    <div className="p-3" style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      height: '100%',
                      border: '1px solid #e9ecef',
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: 'calc(100vh - 200px)',
                      overflowY: 'auto'
                    }}>
                      <div className="mb-3">
                        <div className="text-muted mb-2" style={{ fontSize: '11px' }}>Policy</div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>Procurex Technologies Private Limited</div>
                      </div>

                      <div className="mb-2" style={{ flex: 1 }}>
                        <div className="text-muted mb-2" style={{ fontSize: '11px' }}>Documents</div>
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <input
                            type="file"
                            multiple
                            accept="image/*,.pdf,.doc,.docx"
                            style={{ display: 'none' }}
                            id="document-upload"
                            onChange={(e) => handleDocumentUpload(e.target.files)}
                          />
                          <label
                            htmlFor="document-upload"
                            className="d-flex align-items-center gap-1 text-primary"
                            style={{ cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                          >
                            <Icon name="plus" size="sm" />
                            Upload
                          </label>
                        </div>

                        {/* Uploaded Documents List */}
                        {uploadedDocuments.length > 0 && (
                          <div style={{ flex: 1, overflowY: 'auto', maxHeight: '150px' }} className="custom-scrollbar">
                            {uploadedDocuments.map((file, index) => (
                              <div key={index} className="d-flex justify-content-between align-items-center mb-1 p-2" style={{ backgroundColor: '#fff', borderRadius: '6px', fontSize: '12px' }}>
                                <div className="d-flex align-items-center gap-1">
                                  <Icon
                                    name={file.type.startsWith('image/') ? 'image' : 'file-text'}
                                    className="text-primary"
                                    size="sm"
                                  />
                                  <span className="text-truncate" style={{ maxWidth: '100px' }}>{file.name}</span>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm p-0"
                                  style={{ 
                                    width: '16px', 
                                    height: '16px', 
                                    fontSize: '12px',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#dc3545'
                                  }}
                                  onClick={() => removeDocument(index)}
                                >
                                  <Icon name="trash" size="sm" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </TabPane>

              {/* Advances Tab */}
              <TabPane tabId="advances" style={{ height: '100%' }}>
                <div className="row g-3" style={{ height: '100%', margin: 0 }}>
                  {/* Left Panel - Advance Items */}
                  <div className="col-lg-8 col-md-7" style={{ 
                    height: '100%', 
                    overflowY: 'auto',
                    paddingLeft: '0px',
                    paddingRight: '8px'
                  }}>

                    <div
                      className="border border-2 border-dashed rounded p-3 mb-3"
                      style={{
                        borderColor: isDragOver ? '#1890ff' : '#d1d5db',
                        backgroundColor: isDragOver ? '#f0f8ff' : '#f9fafb',
                        minHeight: '80px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isDragOver) {
                          e.currentTarget.style.borderColor = '#1890ff';
                          e.currentTarget.style.backgroundColor = '#f0f8ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isDragOver) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onClick={() => document.getElementById('advance-receipt-upload')?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const files = e.dataTransfer.files;
                        if (files.length > 0) {
                          handleReceiptUpload(files);
                        }
                      }}
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                        style={{ display: 'none' }}
                        id="advance-receipt-upload"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleReceiptUpload(e.target.files);
                          }
                        }}
                      />
                      <div className="mb-1">
                        <Icon name="upload" size="20" className="text-muted" />
                      </div>
                      <h6 className="mb-0 text-dark fw-normal" style={{ fontSize: '14px' }}>
                        {isDragOver ? 'Drop files here' : 'Drag & drop receipts or click here to upload'}
                      </h6>
                      <small className="text-muted" style={{ fontSize: '12px' }}>
                        Supports: Images, PDF, DOC, XLS (Max 10MB per file)
                      </small>
                    </div>

                    {/* Advance Items */}
                    {loading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "400px",
                        }}
                      >
                        <Spin size="large" tip="Loading advances..." />
                      </div>
                    ) : advances.length > 0 ? (
                      <div style={{ height: '60vh', overflowY: 'auto' }} className="custom-scrollbar">
                        {advances.map((advance) => (
                          <Card key={advance.id} className="mb-3" style={{
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            padding: "16px",
                            border: "1px solid #e9ecef",
                            minHeight: "120px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                          }}>
                            <div className="d-flex align-items-start gap-3">

                              {/* Checkbox - Show for advances that can be selected based on current context */}
                              {((sourcePage === "pendingApproval" && 
                                 advance.paymentStatus?.toLowerCase() === 'pending' && 
                                 isCurrentUserInApprovalHierarchy(advance.expenseApprovals || [])) ||
                                (sourcePage !== "pendingApproval" && 
                                 advance.paymentStatus?.toLowerCase() !== 'approved')) && (
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-2"
                                  style={{ transform: "scale(1.2)" }}
                                  disabled={isCheckboxDisabled(advance)}
                                />
                              )}

                              {/* Advance Details */}
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center justify-content-between mb-2">

                                  {/* Left Section - Category & Badge */}
                                  <div className="d-flex align-items-center gap-2">
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                        fontSize: '9px',
                                        padding: '3px 6px',
                                        fontWeight: '600',
                                        borderRadius: '4px',
                                        backgroundColor: '#e6f7ff',
                                        color: '#1890ff',
                                        border: '1px solid #91d5ff',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                      }}
                                    >
                                      {advance.paymentType === 'Reimbursement' ? 'REF' : 'ADV'}
                                    </span>
                                    <span className="text-primary fw-bold" style={{ fontSize: "15px" }}>
                                      {advance.category}
                                    </span>
                                    {advance.merchant && (
                                      <span className="text-muted" style={{ fontSize: "12px" }}>
                                        • {advance.merchant}
                                      </span>
                                    )}
                                  </div>

                                  {/* Right Section - Amount & Actions */}
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="fw-bold text-dark" style={{
                                      fontSize: "18px",
                                      color: "#2c3e50",
                                      fontFamily: "monospace"
                                    }}>
                                      ₹{advance.amount.toLocaleString()}.00
                                    </div>
                                    <UncontrolledDropdown>
                                      <DropdownToggle
                                        tag="button"
                                        className="btn btn-link btn-sm p-1"
                                        style={{
                                          fontSize: "14px",
                                          color: "#6c757d"
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                        }}
                                      >
                                        <Icon name="more-h" size="sm" />
                                      </DropdownToggle>
                                      <DropdownMenu>
                                        <DropdownItem
                                          style={{ fontSize: "13px", padding: "8px 14px" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleViewEditAdvance(advance, true);
                                          }}
                                        >
                                          <Icon name="eye" className="me-2" size="sm" />
                                          View
                                        </DropdownItem>
                                        {/* Show Edit option for advances regardless of approval status, but only for non-approved reimbursements */}
                                        {(advance.paymentType?.toLowerCase() === 'advance' || advance.paymentStatus?.toLowerCase() !== 'approved') && (
                                          <DropdownItem
                                            style={{ fontSize: "13px", padding: "8px 14px" }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              handleViewEditAdvance(advance, false);
                                            }}
                                          >
                                            <Icon name="edit" className="me-2" size="sm" />
                                            Edit
                                          </DropdownItem>
                                        )}
                                        {/* Show Delete option for advances regardless of approval status, but only for non-approved reimbursements */}
                                        {(advance.paymentType?.toLowerCase() === 'advance' || advance.paymentStatus?.toLowerCase() !== 'approved') && (
                                          <DropdownItem
                                            style={{ fontSize: "13px", padding: "8px 14px" }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              handleDeleteAdvance(advance);
                                            }}
                                          >
                                            <Icon name="trash" className="me-2" size="sm" />
                                            Delete
                                          </DropdownItem>
                                        )}
                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </div>

                                </div>

                                {/* Date and Payment Info */}
                                <div className="d-flex align-items-center gap-3 mb-2">
                                  <div className="text-muted" style={{
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    letterSpacing: "0.3px"
                                  }}>
                                    {advance.date}
                                  </div>
                                  {advance.paymentMode && (
                                    <Badge color="outline-secondary" style={{ fontSize: "10px" }}>
                                      {advance.paymentMode}
                                    </Badge>
                                  )}
                                  {advance.paymentStatus && (
                                    <Tag 
                                      color={
                                        advance.paymentStatus.includes("Draft") ? "geekblue" :
                                        advance.paymentStatus.includes("Pending") ? "gold" :
                                        advance.paymentStatus.includes("Approved") ? "green" : 
                                        advance.paymentStatus.includes("Rejected") ? "red" : "default"
                                      } 
                                      style={{ fontSize: 14, padding: '2px 12px' }}
                                    >
                                      {advance.paymentStatus}
                                    </Tag>
                                  )}
                                </div>
                                {/* Additional Details */}
                                <div className="d-flex align-items-center gap-3 text-muted" style={{ fontSize: "12px" }}>
                                  {advance.taxType && (
                                    <span>Tax: {advance.taxType}</span>
                                  )}
                                  {advance.referenceId && (
                                    <span>Ref: {advance.referenceId}</span>
                                  )}
                                  {advance.items && advance.items.length > 0 && (
                                    <span>{advance.items.length} item{advance.items.length !== 1 ? 's' : ''}</span>
                                  )}
                                  {advance.createdBy && (
                                    <span>Created by: {advance.createdBy}</span>
                                  )}
                                  {/* Reject Reason Display for Advances */}
                                  {advance.expenseApprovals && advance.expenseApprovals.some((approval: ApprovalData) => 
                                    approval.status === 'Rejected' && approval.reason
                                  ) && (
                                    <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
                                      Rejected: {advance.expenseApprovals.find((approval: ApprovalData) => 
                                        approval.status === 'Rejected' && approval.reason
                                      )?.reason}
                                    </span>
                                  )}
                                </div>

                              </div>

                            </div>
                          </Card>
                        ))}
                        
                        {/* Intersection Observer Target for Advances */}
                        {!advancesEndOfList && advances.length >= itemsPerPage && (
                          <div ref={advancesLastEntryRef} style={{ height: '20px' }} />
                        )}
                      </div>
                    ) : (
                      <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "200px",
                        backgroundColor: "white",
                      }}>
                        <Empty description="No Data Found " />
                      </div>
                    )}

                    {/* Summary Section */}
                    <div className="mt-3 pt-2">
                      <div className="row">
                        <div className="col-md-6 text-end">
                          <div className="mb-1" style={{ fontSize: '12px' }}><strong>Total Advances</strong></div>
                          <div className="border-top pt-1" style={{ fontSize: '12px' }}><strong>Unclaimed Advances </strong></div>
                        </div>
                        <div className="col-md-6 text-end">
                          <div className="mb-1" style={{ fontSize: '12px' }}><strong>₹{userTotalIn.toLocaleString()}.00</strong></div>
                          <div className="border-top pt-1" style={{ fontSize: '12px' }}><strong>₹{(reportData?.userUnclaimedAdvanceAmount || 0).toLocaleString()}.00</strong></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - Summary and Actions */}
                  <div className="col-lg-4 col-md-5" style={{
                    paddingLeft: '8px',
                    paddingRight: '0px'
                  }}>
                    <div className="p-3" style={{ 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px', 
                      height: '100%', 
                      border: '1px solid #e9ecef', 
                      display: 'flex', 
                      flexDirection: 'column',
                      maxHeight: 'calc(100vh - 200px)',
                      overflowY: 'auto'
                    }}>
                      <div className="mb-3">
                        <div className="text-muted mb-2" style={{ fontSize: '11px' }}>Policy</div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>Procurex Technologies Private Limited</div>
                      </div>
                      <div className="mb-2" style={{ flex: 1 }}>
                        <div className="text-muted mb-2" style={{ fontSize: '11px' }}>Documents</div>
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <input
                            type="file"
                            multiple
                            accept="image/*,.pdf,.doc,.docx"
                            style={{ display: 'none' }}
                            id="advance-document-upload"
                            onChange={(e) => handleDocumentUpload(e.target.files)}
                          />
                          <label
                            htmlFor="advance-document-upload"
                            className="d-flex align-items-center gap-1 text-primary"
                            style={{ cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                          >
                            <Icon name="plus" size="sm" />
                            Upload
                          </label>
                        </div>

                        {/* Uploaded Documents List */}
                        {uploadedDocuments.length > 0 && (
                          <div className="mt-1 custom-scrollbar" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {uploadedDocuments.map((file, index) => (
                              <div key={index} className="d-flex justify-content-between align-items-center mb-1 p-2" style={{ backgroundColor: '#fff', borderRadius: '6px', fontSize: '12px' }}>
                                <div className="d-flex align-items-center gap-1">
                                  <Icon
                                    name={file.type.startsWith('image/') ? 'image' : 'file-text'}
                                    className="text-primary"
                                    size="sm"
                                  />
                                  <span className="text-truncate" style={{ maxWidth: '100px' }}>{file.name}</span>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm p-0"
                                  style={{ 
                                    width: '16px', 
                                    height: '16px', 
                                    fontSize: '7px',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#dc3545'
                                  }}
                                  onClick={() => removeDocument(index)}
                                >
                                  <Icon name="trash" size="sm" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </TabPane>
            </TabContent>
          </div>
        </form>
      </FormProvider>
      {/* Reject Modal */}
      <Modal isOpen={isRejectModalOpen} toggle={() => setIsRejectModalOpen(false)}>
        <ModalHeader toggle={() => setIsRejectModalOpen(false)}>
          Reject Report
        </ModalHeader>
        <ModalBody>
          <div className="form-group">
            <label className="form-label">Reason for rejection</label>
            <Input
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this report..."
              rows={4}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setIsRejectModalOpen(false);
              setRejectReason('');
            }}
            disabled={isRejecting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleRejectReport}
            disabled={!rejectReason.trim() || isRejecting}
          >
            {isRejecting ? "Rejecting..." : "Reject Report"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Reject Modal */}
      <Modal isOpen={isBulkRejectModalOpen} toggle={() => setIsBulkRejectModalOpen(false)}>
        <ModalHeader toggle={() => setIsBulkRejectModalOpen(false)}>
          Reject Selected Expenses
        </ModalHeader>
        <ModalBody>
          <div className="form-group">
            <label className="form-label">Reason for rejection</label>
            <Input
              type="textarea"
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejecting these expenses..."
              rows={4}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setIsBulkRejectModalOpen(false);
              setBulkRejectReason('');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleBulkRejectExpenses}
            disabled={!bulkRejectReason.trim() || loading}
          >
            {loading ? "Rejecting..." : "Reject Selected"}
          </Button>
        </ModalFooter>
      </Modal>

    </>
  );
};

export default ReportBreakDown;